import React, { useState, useEffect, useRef } from "react";

const SHOW_DURATION_MS = 3000;

export default function Level3Memory({ questions, sound, onComplete }) {
  const [qIndex, setQIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [stage, setStage] = useState("show"); // show | ask
  const [feedback, setFeedback] = useState(null);
  const startTimeRef = useRef(Date.now());

  const q = questions[qIndex];
  const total = questions.length;

  useEffect(() => { startTimeRef.current = Date.now(); }, []);

  useEffect(() => {
    setStage("show");
    const t = setTimeout(() => setStage("ask"), SHOW_DURATION_MS);
    return () => clearTimeout(t);
  }, [qIndex]);

  const handleTap = (option) => {
    if (feedback) return;
    const isCorrect = option === q.answer;
    sound[isCorrect ? "correct" : "wrong"]();
    setFeedback({ option, correct: isCorrect });
    if (isCorrect) setCorrectCount((c) => c + 1);

    setTimeout(() => {
      setFeedback(null);
      if (qIndex + 1 < total) {
        setQIndex((i) => i + 1);
      } else {
        const timeTakenSec = Math.round((Date.now() - startTimeRef.current) / 1000);
        onComplete({
          correctCount: correctCount + (isCorrect ? 1 : 0),
          totalQuestions: total,
          timeTakenSec,
          hintsUsed: 0,
        });
      }
    }, 700);
  };

  if (!q) return null;

  return (
    <div className="flex flex-col items-center gap-6 py-6 px-4">
      {stage === "show" ? (
        <>
          <p className="text-base sm:text-lg font-bold text-cyan-100 text-center">Remember these! 👀</p>
          <div className="flex gap-4 sm:gap-6">
            {q.showSet.map((emoji, idx) => (
              <div key={idx} className="text-5xl sm:text-6xl animate-[pop_0.3s_ease-out]">{emoji}</div>
            ))}
          </div>
          <div className="w-full max-w-xs h-1.5 bg-[#F3EEFF] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#7C3AED] rounded-full"
              style={{ animation: `shrink ${SHOW_DURATION_MS}ms linear forwards` }}
            />
          </div>
        </>
      ) : (
        <>
          <p className="text-lg sm:text-xl font-bold text-cyan-100 text-center">{q.question}</p>
          <div className="grid grid-cols-3 gap-3 w-full max-w-sm">
            {q.options.map((opt, idx) => {
              const isTapped = feedback?.option === opt;
              const showState = isTapped ? (feedback.correct ? "correct" : "wrong") : null;
              return (
                <button
                  key={idx}
                  onClick={() => handleTap(opt)}
                  disabled={!!feedback}
                  className={[
                    "rounded-2xl py-4 text-sm sm:text-base font-semibold shadow-sm transition-all",
                    showState === "correct" ? "bg-[#DCFCE7] ring-4 ring-[#4ADE80]" :
                    showState === "wrong" ? "bg-[#FEE2E2] ring-4 ring-red-300" :
                    "bg-gray-800 text-gray-100 active:scale-95",
                  ].join(" ")}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </>
      )}
      <p className="text-xs text-gray-100">Question {qIndex + 1} of {total}</p>
      <style>{`@keyframes shrink { from { width: 100%; } to { width: 0%; } }`}</style>
    </div>
  );
}
